/**
 * Content Page Component
 * Dashboard, content reports, market proposals, bet ordering, notifications, campaigns
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../store/AdminContext';
import { apiService } from '../services/apiService';
import ConfirmModal from '../components/modals/ConfirmModal';
import './ContentPage.css';

interface ContentReport {
  id: string;
  userId: string;
  contentType: string;
  contentId: string;
  reason: string;
  details: string | null;
  status: string;
  createdAt: string;
  reporterEmail?: string;
  reporterUsername?: string;
}

interface MarketProposal {
  id: string;
  user_id: string;
  headline: string;
  description: string;
  category: string;
  answer_type: string;
  status: string;
  created_at: string;
}

interface ContentPageProps {
  view: string;
}

const slugFromTitle = (title: string) =>
  title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);

const OriginalsView: React.FC = () => {
  const [originals, setOriginals] = useState<Array<{ id: string; title: string; slug: string; type: string; publishedAt: string | null }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createSlug, setCreateSlug] = useState('');
  const [createType, setCreateType] = useState<'article' | 'video'>('article');
  const [createContent, setCreateContent] = useState('');
  const [createMediaUrl, setCreateMediaUrl] = useState('');
  const [createPublishNow, setCreatePublishNow] = useState(true);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [creating, setCreating] = useState(false);
  const [publishingId, setPublishingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getOriginalsAdmin();
      setOriginals(res.originals || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeCreateModal = () => {
    setShowCreate(false);
    setCreateTitle('');
    setCreateSlug('');
    setCreateContent('');
    setCreateMediaUrl('');
    setSlugManuallyEdited(false);
  };

  const handleTitleChange = (value: string) => {
    setCreateTitle(value);
    if (!slugManuallyEdited) setCreateSlug(slugFromTitle(value));
  };

  const handleCreate = async () => {
    const slug = (createSlug.trim() || slugFromTitle(createTitle)).replace(/-+/g, '-');
    if (!createTitle.trim() || !slug) return;
    setCreating(true);
    try {
      await apiService.createOriginal({
        title: createTitle.trim(),
        slug,
        type: createType,
        content: createContent.trim() || undefined,
        mediaUrl: createMediaUrl.trim() || undefined,
        publishedAt: createPublishNow ? new Date().toISOString() : undefined,
      });
      closeCreateModal();
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (id: string) => {
    setPublishingId(id);
    try {
      await apiService.updateOriginal(id, { publishedAt: new Date().toISOString() });
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish');
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <div className="content-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Versus Originals</h1>
          <p className="page-sub">Curated articles and videos</p>
        </div>
        <button className="btn btn-primary create-original-btn" onClick={() => setShowCreate(true)}>
          <span className="btn-icon">+</span>
          Create Original
        </button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="content-card">
        <div className="card-body">
          {loading ? <div className="content-loading">Loading...</div> : originals.length === 0 ? (
            <div className="content-empty">No originals yet</div>
          ) : (
            <div className="table-wrap">
              <table className="content-table">
                <thead><tr><th>Title</th><th>Slug</th><th>Type</th><th>Published</th><th>Actions</th></tr></thead>
                <tbody>
                  {originals.map((o) => (
                    <tr key={o.id}>
                      <td>{o.title}</td><td>{o.slug}</td><td>{o.type}</td>
                      <td>{o.publishedAt ? new Date(o.publishedAt).toLocaleDateString() : <span className="status yellow">Draft</span>}</td>
                      <td>
                        {!o.publishedAt && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handlePublish(o.id)}
                            disabled={publishingId === o.id}
                          >
                            {publishingId === o.id ? 'Publishing...' : 'Publish'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {showCreate && (
        <div className="modal-overlay" onClick={closeCreateModal}>
          <div className="modal-content create-original-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📰 Create Original</h2>
              <button className="modal-close" onClick={closeCreateModal} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  className="form-input"
                  value={createTitle}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="e.g. Bitcoin Price Prediction 2025"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>URL Slug *</label>
                <input
                  type="text"
                  className="form-input"
                  value={createSlug}
                  onChange={(e) => { setCreateSlug(e.target.value); setSlugManuallyEdited(true); }}
                  placeholder="bitcoin-price-prediction-2025"
                />
                <span className="form-hint">Used in /originals/{createSlug || 'your-slug'}</span>
              </div>
              <div className="form-group">
                <label>Type</label>
                <div className="create-original-type-selector">
                  <button
                    type="button"
                    className={`type-option ${createType === 'article' ? 'active' : ''}`}
                    onClick={() => setCreateType('article')}
                  >
                    <span className="type-icon">📄</span>
                    <span>Article</span>
                  </button>
                  <button
                    type="button"
                    className={`type-option ${createType === 'video' ? 'active' : ''}`}
                    onClick={() => setCreateType('video')}
                  >
                    <span className="type-icon">🎬</span>
                    <span>Video</span>
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea
                  className="form-input form-textarea"
                  value={createContent}
                  onChange={(e) => setCreateContent(e.target.value)}
                  placeholder="Article body or video description..."
                  rows={4}
                />
              </div>
              <div className="form-group">
                <label>Media URL</label>
                <input
                  type="url"
                  className="form-input"
                  value={createMediaUrl}
                  onChange={(e) => setCreateMediaUrl(e.target.value)}
                  placeholder="https://... (image or video embed URL)"
                />
              </div>
              <div className="form-group form-check">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={createPublishNow}
                    onChange={(e) => setCreatePublishNow(e.target.checked)}
                  />
                  <span>Publish immediately (visible to users)</span>
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeCreateModal}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={creating || !createTitle.trim()}>
                {creating ? 'Creating...' : 'Create Original'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AwardsView: React.FC = () => {
  const [badges, setBadges] = useState<Array<{ id: string; name: string; description: string | null; rewardAmountCents?: number | null; creditsWithdrawable?: boolean }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAward, setShowAward] = useState(false);
  const [showCreateBadge, setShowCreateBadge] = useState(false);
  const [awardUserId, setAwardUserId] = useState('');
  const [awardBadgeId, setAwardBadgeId] = useState('');
  const [awarding, setAwarding] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createIcon, setCreateIcon] = useState('');
  const [createRewardCents, setCreateRewardCents] = useState<string>('');
  const [createCreditsWithdrawable, setCreateCreditsWithdrawable] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getBadgeDefinitions();
      setBadges(res.badges || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeAwardModal = () => {
    setShowAward(false);
    setAwardUserId('');
    setAwardBadgeId('');
  };

  const closeCreateBadgeModal = () => {
    setShowCreateBadge(false);
    setCreateTitle('');
    setCreateDesc('');
    setCreateIcon('');
    setCreateRewardCents('');
    setCreateCreditsWithdrawable(true);
  };

  const handleCreateBadge = async () => {
    if (!createTitle.trim()) return;
    setCreating(true);
    try {
      const rewardCents = createRewardCents.trim() ? parseInt(createRewardCents, 10) : undefined;
      await apiService.createBadge({
        name: createTitle.trim(),
        description: createDesc.trim() || undefined,
        icon: createIcon.trim() || undefined,
        rewardAmountCents: rewardCents != null && !Number.isNaN(rewardCents) ? rewardCents : undefined,
        creditsWithdrawable: createCreditsWithdrawable,
      });
      closeCreateBadgeModal();
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create badge');
    } finally {
      setCreating(false);
    }
  };

  const handleAward = async () => {
    if (!awardUserId.trim() || !awardBadgeId) return;
    setAwarding(true);
    try {
      await apiService.awardBadge(awardUserId.trim(), awardBadgeId);
      closeAwardModal();
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to award badge');
    } finally {
      setAwarding(false);
    }
  };

  return (
    <div className="content-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Versus Awards</h1>
          <p className="page-sub">Badge definitions and manual awards</p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-primary create-badge-btn" onClick={() => setShowCreateBadge(true)}>
            <span className="btn-icon">+</span>
            Create Badge
          </button>
          <button className="btn btn-primary award-badge-btn" onClick={() => setShowAward(true)}>
            <span className="btn-icon">🏆</span>
            Award Badge
          </button>
        </div>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="content-card">
        <div className="card-body">
          {loading ? <div className="content-loading">Loading...</div> : badges.length === 0 ? (
            <div className="content-empty">No badge definitions yet. Create your first badge above.</div>
          ) : (
            <div className="table-wrap">
              <table className="content-table">
                <thead><tr><th>Name</th><th>Description</th></tr></thead>
                <tbody>
                  {badges.map((b) => (
                    <tr key={b.id}><td>{b.name}</td><td>{b.description || '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {showAward && (
        <div className="modal-overlay" onClick={closeAwardModal}>
          <div className="modal-content award-badge-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏆 Award Badge</h2>
              <button className="modal-close" onClick={closeAwardModal} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>User ID *</label>
                <input
                  type="text"
                  className="form-input"
                  value={awardUserId}
                  onChange={(e) => setAwardUserId(e.target.value)}
                  placeholder="e.g. 885d8bb6-bce6-44e1-ba46-655256648482"
                  autoFocus
                />
                <span className="form-hint">The UUID of the user to award the badge to</span>
              </div>
              <div className="form-group">
                <label>Badge *</label>
                <select
                  className="form-input"
                  value={awardBadgeId}
                  onChange={(e) => setAwardBadgeId(e.target.value)}
                >
                  <option value="">Select badge...</option>
                  {badges.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                {awardBadgeId && badges.find((b) => b.id === awardBadgeId)?.description && (
                  <span className="form-hint">{badges.find((b) => b.id === awardBadgeId)?.description}</span>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeAwardModal}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleAward} disabled={awarding || !awardUserId.trim() || !awardBadgeId}>
                {awarding ? 'Awarding...' : 'Award Badge'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCreateBadge && (
        <div className="modal-overlay" onClick={closeCreateBadgeModal}>
          <div className="modal-content create-badge-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏆 Create Badge</h2>
              <button className="modal-close" onClick={closeCreateBadgeModal} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={createTitle}
                  onChange={(e) => setCreateTitle(e.target.value)}
                  placeholder="e.g. First Bet, Top Trader"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={createDesc}
                  onChange={(e) => setCreateDesc(e.target.value)}
                  placeholder="What this badge represents"
                />
              </div>
              <div className="form-group">
                <label>Icon</label>
                <input
                  type="text"
                  className="form-input"
                  value={createIcon}
                  onChange={(e) => setCreateIcon(e.target.value)}
                  placeholder="e.g. 🏆 or 🎯"
                />
                <span className="form-hint">Emoji or icon identifier</span>
              </div>
              <div className="form-group">
                <label>Credits amount (cents)</label>
                <input
                  type="number"
                  className="form-input"
                  value={createRewardCents}
                  onChange={(e) => setCreateRewardCents(e.target.value)}
                  placeholder="e.g. 100 = £1.00"
                  min={0}
                />
                <span className="form-hint">Optional. Leave empty for no credit reward.</span>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={createCreditsWithdrawable}
                    onChange={(e) => setCreateCreditsWithdrawable(e.target.checked)}
                  />
                  Credits withdrawable
                </label>
                <span className="form-hint">If unchecked, reward goes to bonus balance (non-withdrawable).</span>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeCreateBadgeModal}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleCreateBadge} disabled={creating || !createTitle.trim()}>
                {creating ? 'Creating...' : 'Create Badge'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AcademyView: React.FC = () => {
  const [modules, setModules] = useState<Array<{ id: string; title: string; lessons: Array<{ id: string; title: string }> }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModule, setShowModule] = useState(false);
  const [showLesson, setShowLesson] = useState(false);
  const [moduleTitle, setModuleTitle] = useState('');
  const [lessonModuleId, setLessonModuleId] = useState('');
  const [lessonTitle, setLessonTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [expandedModule, setExpandedModule] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getAcademyModules();
      setModules(res.modules || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeModuleModal = () => {
    setShowModule(false);
    setModuleTitle('');
  };

  const closeLessonModal = () => {
    setShowLesson(false);
    setLessonModuleId('');
    setLessonTitle('');
  };

  const handleCreateModule = async () => {
    if (!moduleTitle.trim()) return;
    setCreating(true);
    try {
      await apiService.createAcademyModule({ title: moduleTitle.trim() });
      closeModuleModal();
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create module');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateLesson = async () => {
    if (!lessonModuleId || !lessonTitle.trim()) return;
    setCreating(true);
    try {
      await apiService.createAcademyLesson({ moduleId: lessonModuleId, title: lessonTitle.trim() });
      closeLessonModal();
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create lesson');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="content-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Versus Academy</h1>
          <p className="page-sub">Learning modules and lessons</p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-secondary create-badge-btn" onClick={() => setShowModule(true)}>
            <span className="btn-icon">📚</span>
            Add Module
          </button>
          <button className="btn btn-primary create-badge-btn" onClick={() => setShowLesson(true)}>
            <span className="btn-icon">+</span>
            Add Lesson
          </button>
        </div>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="content-card">
        <div className="card-body">
          {loading ? (
            <div className="content-loading">Loading...</div>
          ) : modules.length === 0 ? (
            <div className="content-empty">No modules yet. Create your first module above.</div>
          ) : (
            <div className="content-list academy-list">
              {modules.map((m) => (
                <div key={m.id} className="content-item academy-module-item">
                  <div className="academy-module-row">
                    <div
                      className="content-item-main academy-module-main"
                      onClick={() => setExpandedModule(expandedModule === m.id ? null : m.id)}
                    >
                      <div className="content-item-title">{m.title}</div>
                      <div className="content-item-meta">{m.lessons?.length ?? 0} lessons</div>
                    </div>
                    <div className="content-item-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={(e) => { e.stopPropagation(); setLessonModuleId(m.id); setShowLesson(true); }}
                      >
                        Add Lesson
                      </button>
                      {m.lessons && m.lessons.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary academy-expand-btn"
                          onClick={(e) => { e.stopPropagation(); setExpandedModule(expandedModule === m.id ? null : m.id); }}
                          aria-expanded={expandedModule === m.id}
                        >
                          {expandedModule === m.id ? '▼' : '▶'}
                        </button>
                      )}
                    </div>
                  </div>
                  {expandedModule === m.id && m.lessons && m.lessons.length > 0 && (
                    <div className="academy-lessons">
                      {m.lessons.map((l, i) => (
                        <div key={l.id} className="academy-lesson-item">
                          <span className="academy-lesson-num">{i + 1}</span>
                          <span className="academy-lesson-title">{l.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showModule && (
        <div className="modal-overlay" onClick={closeModuleModal}>
          <div className="modal-content create-badge-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📚 Add Module</h2>
              <button className="modal-close" onClick={closeModuleModal} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  className="form-input"
                  value={moduleTitle}
                  onChange={(e) => setModuleTitle(e.target.value)}
                  placeholder="e.g. Introduction to Prediction Markets"
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeModuleModal}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleCreateModule} disabled={creating || !moduleTitle.trim()}>
                {creating ? 'Creating...' : 'Add Module'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showLesson && (
        <div className="modal-overlay" onClick={closeLessonModal}>
          <div className="modal-content create-badge-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Lesson</h2>
              <button className="modal-close" onClick={closeLessonModal} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Module *</label>
                <select
                  className="form-input"
                  value={lessonModuleId}
                  onChange={(e) => setLessonModuleId(e.target.value)}
                >
                  <option value="">Select module...</option>
                  {modules.map((m) => (
                    <option key={m.id} value={m.id}>{m.title}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Lesson Title *</label>
                <input
                  type="text"
                  className="form-input"
                  value={lessonTitle}
                  onChange={(e) => setLessonTitle(e.target.value)}
                  placeholder="e.g. What are prediction markets?"
                  autoFocus={!!lessonModuleId}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={closeLessonModal}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleCreateLesson} disabled={creating || !lessonModuleId || !lessonTitle.trim()}>
                {creating ? 'Creating...' : 'Add Lesson'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ContentPage: React.FC<ContentPageProps> = ({ view }) => {
  const { setCurrentPage } = useAdmin();
  const [reports, setReports] = useState<ContentReport[]>([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [proposals, setProposals] = useState<MarketProposal[]>([]);
  const [proposalsTotal, setProposalsTotal] = useState(0);
  const [markets, setMarkets] = useState<any[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [reportStatusFilter, setReportStatusFilter] = useState<string>('pending');
  const [resolveModal, setResolveModal] = useState<{ report: ContentReport; action: 'resolved' | 'dismissed' } | null>(null);
  const [rejectModal, setRejectModal] = useState<{ proposal: MarketProposal; reason: string } | null>(null);
  const [processing, setProcessing] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [reportsRes, proposalsRes, marketsRes, pendingRes] = await Promise.all([
        apiService.getContentReports({ status: 'pending', limit: 5, offset: 0 }),
        apiService.getMarketProposals({ status: 'pending_review', limit: 5, offset: 0 }),
        apiService.getAllMarkets({ status: 'live' }),
        apiService.getPendingMarkets(),
      ]);
      setReports(reportsRes.reports || []);
      setReportsTotal(reportsRes.total ?? 0);
      setProposals(proposalsRes.proposals || []);
      setProposalsTotal(proposalsRes.total ?? 0);
      setMarkets(marketsRes.markets || []);
      setPendingCount((pendingRes.markets || []).length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { reports: data, total } = await apiService.getContentReports({
        status: reportStatusFilter ? (reportStatusFilter as 'pending' | 'resolved' | 'dismissed') : undefined,
        limit: 50,
        offset: 0,
      });
      setReports(data || []);
      setReportsTotal(total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
      setReports([]);
      setReportsTotal(0);
    } finally {
      setLoading(false);
    }
  }, [reportStatusFilter]);

  const loadProposals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { proposals: data, total } = await apiService.getMarketProposals({
        status: statusFilter ? (statusFilter as 'pending_review' | 'approved' | 'rejected') : undefined,
        limit: 50,
        offset: 0,
      });
      setProposals(data || []);
      setProposalsTotal(total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load proposals');
      setProposals([]);
      setProposalsTotal(0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (view === 'content-dashboard') {
      loadDashboard();
    } else if (view === 'content-reports') {
      loadReports();
    } else if (view === 'market-proposals') {
      loadProposals();
    } else {
      setLoading(false);
    }
  }, [view, loadDashboard, loadReports, loadProposals]);

  const handleResolveReport = async () => {
    if (!resolveModal || processing) return;
    setProcessing(true);
    try {
      await apiService.resolveContentReport(resolveModal.report.id, resolveModal.action);
      setResolveModal(null);
      if (view === 'content-dashboard') loadDashboard();
      else loadReports();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve report');
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveProposal = async (id: string) => {
    try {
      await apiService.approveMarketProposal(id);
      if (view === 'content-dashboard') loadDashboard();
      else loadProposals();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve proposal');
    }
  };

  const handleRejectProposal = async () => {
    if (!rejectModal || processing) return;
    setProcessing(true);
    try {
      await apiService.rejectMarketProposal(rejectModal.proposal.id, rejectModal.reason || undefined);
      setRejectModal(null);
      if (view === 'content-dashboard') loadDashboard();
      else loadProposals();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject proposal');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (d: string) => (d ? new Date(d).toLocaleString() : '—');

  // --- Content Dashboard ---
  if (view === 'content-dashboard') {
    return (
      <div className="content-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">Content Dashboard</h1>
            <p className="page-sub">Markets, moderation, and content overview</p>
          </div>
        </div>

        <div className="content-stats">
          <div className="content-stat clickable" onClick={() => setCurrentPage('predictions-overview')}>
            <div className="content-stat-icon cyan">🎯</div>
            <div className="content-stat-val">{markets.length}</div>
            <div className="content-stat-label">Live Markets</div>
          </div>
          <div className="content-stat clickable" onClick={() => setCurrentPage('pending-approval')}>
            <div className="content-stat-icon orange">⏳</div>
            <div className="content-stat-val">{pendingCount}</div>
            <div className="content-stat-label">Pending Approval</div>
          </div>
          <div className="content-stat clickable" onClick={() => setCurrentPage('market-proposals')}>
            <div className="content-stat-icon purple">📝</div>
            <div className="content-stat-val">{proposalsTotal}</div>
            <div className="content-stat-label">Market Proposals</div>
          </div>
          <div className="content-stat clickable" onClick={() => setCurrentPage('content-reports')}>
            <div className="content-stat-icon red">🚩</div>
            <div className="content-stat-val">{reportsTotal}</div>
            <div className="content-stat-label">Content Reports</div>
          </div>
        </div>

        <div className="content-grid">
          <div className="content-card">
            <div className="card-head">
              <span className="card-title">Pending Reports</span>
              <button className="btn btn-secondary" onClick={() => setCurrentPage('content-reports')}>
                View All
              </button>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="content-loading">Loading...</div>
              ) : reports.length === 0 ? (
                <div className="content-empty">No pending reports</div>
              ) : (
                <div className="content-list">
                  {reports.map((r) => (
                    <div key={r.id} className="content-item">
                      <div className="content-item-main">
                        <div className="content-item-title">{r.reason}</div>
                        <div className="content-item-meta">
                          {r.contentType} · {r.reporterUsername || r.reporterEmail || r.userId} · {formatDate(r.createdAt)}
                        </div>
                      </div>
                      <div className="content-item-actions">
                        <button className="btn btn-sm btn-success" onClick={() => setResolveModal({ report: r, action: 'resolved' })}>Resolve</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => setResolveModal({ report: r, action: 'dismissed' })}>Dismiss</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="content-card">
            <div className="card-head">
              <span className="card-title">Market Proposals</span>
              <button className="btn btn-secondary" onClick={() => setCurrentPage('market-proposals')}>
                View All
              </button>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="content-loading">Loading...</div>
              ) : proposals.length === 0 ? (
                <div className="content-empty">No pending proposals</div>
              ) : (
                <div className="content-list">
                  {proposals.map((p) => (
                    <div key={p.id} className="content-item">
                      <div className="content-item-main">
                        <div className="content-item-title">{p.headline}</div>
                        <div className="content-item-meta">{p.category} · {formatDate(p.created_at)}</div>
                      </div>
                      <div className="content-item-actions">
                        <button className="btn btn-sm btn-success" onClick={() => handleApproveProposal(p.id)}>Approve</button>
                        <button className="btn btn-sm btn-danger" onClick={() => setRejectModal({ proposal: p, reason: '' })}>Reject</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {resolveModal && (
          <ConfirmModal
            isOpen={!!resolveModal}
            title={resolveModal.action === 'resolved' ? 'Resolve Report' : 'Dismiss Report'}
            message={`Mark this report as ${resolveModal.action}?`}
            confirmText={resolveModal.action === 'resolved' ? 'Resolve' : 'Dismiss'}
            confirmButtonClass={resolveModal.action === 'resolved' ? 'success' : 'primary'}
            onConfirm={handleResolveReport}
            onCancel={() => setResolveModal(null)}
          />
        )}

        {rejectModal && (
          <div className="modal-overlay" onClick={() => setRejectModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Reject Proposal</h2>
                <button className="modal-close" onClick={() => setRejectModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: 16 }}>Reject &quot;{rejectModal.proposal.headline}&quot;?</p>
                <div className="form-group">
                  <label>Reason (optional)</label>
                  <textarea value={rejectModal.reason} onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })} rows={3} placeholder="Reason for rejection..." />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleRejectProposal} disabled={processing}>{processing ? 'Rejecting...' : 'Reject'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Content Reports ---
  if (view === 'content-reports') {
    return (
      <div className="content-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">Content Reports</h1>
            <p className="page-sub">User-submitted content reports for moderation</p>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="filter-row">
          <label>Status:</label>
          <select value={reportStatusFilter} onChange={(e) => setReportStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>

        <div className="content-card">
          <div className="card-body">
            {loading ? (
              <div className="content-loading">Loading...</div>
            ) : reports.length === 0 ? (
              <div className="content-empty">No reports found</div>
            ) : (
              <div className="table-wrap">
                <table className="content-table">
                  <thead>
                    <tr>
                      <th>Reason</th>
                      <th>Type</th>
                      <th>Reporter</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((r) => (
                      <tr key={r.id}>
                        <td className="report-reason">{r.reason}</td>
                        <td>{r.contentType}</td>
                        <td>{r.reporterUsername || r.reporterEmail || r.userId}</td>
                        <td><span className={`status-badge status-${r.status}`}>{r.status}</span></td>
                        <td>{formatDate(r.createdAt)}</td>
                        <td>
                          {r.status === 'pending' && (
                            <>
                              <button className="btn btn-sm btn-success" onClick={() => setResolveModal({ report: r, action: 'resolved' })}>Resolve</button>
                              <button className="btn btn-sm btn-secondary" onClick={() => setResolveModal({ report: r, action: 'dismissed' })}>Dismiss</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {resolveModal && (
          <ConfirmModal
            isOpen={!!resolveModal}
            title={resolveModal.action === 'resolved' ? 'Resolve Report' : 'Dismiss Report'}
            message={`Mark this report as ${resolveModal.action}?`}
            confirmText={resolveModal.action === 'resolved' ? 'Resolve' : 'Dismiss'}
            confirmButtonClass={resolveModal.action === 'resolved' ? 'success' : 'primary'}
            onConfirm={handleResolveReport}
            onCancel={() => setResolveModal(null)}
          />
        )}
      </div>
    );
  }

  // --- Market Proposals ---
  if (view === 'market-proposals') {
    return (
      <div className="content-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">Market Proposals</h1>
            <p className="page-sub">User-submitted market proposals for review</p>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="filter-row">
          <label>Status:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="pending_review">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="content-card">
          <div className="card-body">
            {loading ? (
              <div className="content-loading">Loading...</div>
            ) : proposals.length === 0 ? (
              <div className="content-empty">No proposals found</div>
            ) : (
              <div className="content-list proposals-list">
                {proposals.map((p) => (
                  <div key={p.id} className="content-item proposal-item">
                    <div className="content-item-main">
                      <div className="content-item-title">{p.headline}</div>
                      <div className="content-item-desc">{p.description}</div>
                      <div className="content-item-meta">{p.category} · {p.answer_type} · {formatDate(p.created_at)}</div>
                    </div>
                    <div className="content-item-actions">
                      <span className={`status-badge status-${p.status || 'unknown'}`}>{(p.status || '').replace('_', ' ')}</span>
                      {p.status === 'pending_review' && (
                        <>
                          <button className="btn btn-sm btn-success" onClick={() => handleApproveProposal(p.id)}>Approve</button>
                          <button className="btn btn-sm btn-danger" onClick={() => setRejectModal({ proposal: p, reason: '' })}>Reject</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {rejectModal && (
          <div className="modal-overlay" onClick={() => setRejectModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Reject Proposal</h2>
                <button className="modal-close" onClick={() => setRejectModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: 16 }}>Reject &quot;{rejectModal.proposal.headline}&quot;?</p>
                <div className="form-group">
                  <label>Reason (optional)</label>
                  <textarea value={rejectModal.reason} onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })} rows={3} placeholder="Reason for rejection..." />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleRejectProposal} disabled={processing}>{processing ? 'Rejecting...' : 'Reject'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Challenges ---
  const ChallengesView: React.FC = () => {
    const [challenges, setChallenges] = useState<Array<{
      id: string;
      userId: string;
      username: string;
      userEmail: string;
      title: string;
      description: string | null;
      status: string;
      createdAt: string;
    }>>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [rejectModal, setRejectModal] = useState<{ challenge: { id: string; title: string }; reason: string } | null>(null);
    const [processing, setProcessing] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [createTitle, setCreateTitle] = useState('');
    const [createDesc, setCreateDesc] = useState('');
    const [createUserId, setCreateUserId] = useState('');
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiService.getChallenges({
          status: statusFilter || undefined,
          limit: 50,
        });
        setChallenges(res.challenges || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const handleApprove = async (id: string) => {
      setProcessing(true);
      try {
        await apiService.approveChallenge(id);
        load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to approve');
      } finally {
        setProcessing(false);
      }
    };

    const handleReject = async () => {
      if (!rejectModal) return;
      setProcessing(true);
      try {
        await apiService.rejectChallenge(rejectModal.challenge.id, rejectModal.reason || undefined);
        setRejectModal(null);
        load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to reject');
      } finally {
        setProcessing(false);
      }
    };

    const closeCreateModal = () => {
      setShowCreate(false);
      setCreateTitle('');
      setCreateDesc('');
      setCreateUserId('');
    };

    const handleCreate = async () => {
      if (!createTitle.trim() || !createUserId.trim()) return;
      setCreating(true);
      try {
        await apiService.createChallenge({
          title: createTitle.trim(),
          description: createDesc.trim() || undefined,
          userId: createUserId.trim(),
        });
        closeCreateModal();
        load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create challenge');
      } finally {
        setCreating(false);
      }
    };

    const formatDate = (d: string) => new Date(d).toLocaleDateString();

    return (
      <div className="content-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">Challenges</h1>
            <p className="page-sub">User-created prediction challenges for review</p>
          </div>
          <div className="page-head-actions">
            <button className="btn btn-primary create-badge-btn" onClick={() => setShowCreate(true)}>
              <span className="btn-icon">+</span>
              Create Challenge
            </button>
          </div>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        <div className="filter-row">
          <label>Status:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="content-card">
          <div className="card-body">
            {loading ? (
              <div className="content-loading">Loading...</div>
            ) : challenges.length === 0 ? (
              <div className="content-empty">No challenges found</div>
            ) : (
              <div className="content-list">
                {challenges.map((c) => (
                  <div key={c.id} className="content-item">
                    <div className="content-item-main">
                      <div className="content-item-title">{c.title}</div>
                      {c.description && <div className="content-item-desc">{c.description}</div>}
                      <div className="content-item-meta">{c.username} · {formatDate(c.createdAt)}</div>
                    </div>
                    <div className="content-item-actions">
                      <span className={`status-badge status-${c.status}`}>{c.status}</span>
                      {c.status === 'pending' && (
                        <>
                          <button className="btn btn-sm btn-success" onClick={() => handleApprove(c.id)} disabled={processing}>Approve</button>
                          <button className="btn btn-sm btn-danger" onClick={() => setRejectModal({ challenge: c, reason: '' })} disabled={processing}>Reject</button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {rejectModal && (
          <div className="modal-overlay" onClick={() => setRejectModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Reject Challenge</h2>
                <button className="modal-close" onClick={() => setRejectModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: 16 }}>Reject &quot;{rejectModal.challenge.title}&quot;?</p>
                <div className="form-group">
                  <label>Reason (optional)</label>
                  <textarea value={rejectModal.reason} onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })} rows={3} placeholder="Reason for rejection..." />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleReject} disabled={processing}>{processing ? 'Rejecting...' : 'Reject'}</button>
              </div>
            </div>
          </div>
        )}
        {showCreate && (
          <div className="modal-overlay" onClick={closeCreateModal}>
            <div className="modal-content create-badge-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Create Challenge</h2>
                <button className="modal-close" onClick={closeCreateModal} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={createTitle}
                    onChange={(e) => setCreateTitle(e.target.value)}
                    placeholder="e.g. Will Bitcoin hit $100k by end of 2025?"
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Description (optional)</label>
                  <textarea
                    className="form-input"
                    value={createDesc}
                    onChange={(e) => setCreateDesc(e.target.value)}
                    rows={3}
                    placeholder="Additional context..."
                  />
                </div>
                <div className="form-group">
                  <label>User ID *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={createUserId}
                    onChange={(e) => setCreateUserId(e.target.value)}
                    placeholder="e.g. 885d8bb6-bce6-44e1-ba46-655256648482"
                  />
                  <span className="form-hint">The UUID of the user who owns this challenge (from Users page)</span>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeCreateModal}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={creating || !createTitle.trim() || !createUserId.trim()}>
                  {creating ? 'Creating...' : 'Create Challenge'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- Versus Originals ---
  if (view === 'originals') {
    return <OriginalsView />;
  }

  // --- Versus Awards ---
  if (view === 'awards') {
    return <AwardsView />;
  }

  // --- Versus Academy ---
  if (view === 'academy') {
    return <AcademyView />;
  }

  if (view === 'challenges') {
    return <ChallengesView />;
  }

  // --- Bet Ordering ---
  if (view === 'bet-ordering') {
    return (
      <div className="content-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">Bet Ordering</h1>
            <p className="page-sub">Configure display order of markets and betting options</p>
          </div>
        </div>
        <div className="content-card">
          <div className="card-body">
            <div className="content-placeholder">
              <div className="placeholder-icon">📋</div>
              <p>Drag and drop to reorder markets. Feature coming soon.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Notifications / Campaigns (placeholder) ---
  if (view === 'notifications' || view === 'campaigns') {
    const title = view === 'notifications' ? 'Push Notifications' : 'Campaigns';
    const icon = view === 'notifications' ? '🔔' : '📣';
    return (
      <div className="content-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">{title}</h1>
            <p className="page-sub">Coming soon</p>
          </div>
        </div>
        <div className="content-card">
          <div className="card-body">
            <div className="content-placeholder">
              <div className="placeholder-icon">{icon}</div>
              <p>{view === 'notifications' ? 'Send push notifications to users' : 'Create and manage marketing campaigns'}</p>
              <p className="placeholder-hint">This feature is coming soon.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-page">
      <div className="page-head">
        <h1 className="page-title">Content</h1>
        <p className="page-sub">Content management</p>
      </div>
      <div className="content-card">
        <div className="card-body">
          <p style={{ color: 'var(--text-muted)' }}>Unknown view: {view}</p>
        </div>
      </div>
    </div>
  );
};

export default ContentPage;
